import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Alert, Button, Card, CardContent, Typography } from '@/ui';

import {
  useGetAuthStatusQuery,
  useSendEmailVerificationMutation,
} from '@/store/api/authApi';
import {
  normalizePostAuthRedirect,
  redirectToSignin,
} from '@/utils/authRedirect';
import { track } from '@/utils/tracking';

const MFA_PROMPT_SKIPPED_UNTIL_KEY = 'mfa_prompt_skipped_until';
const EMAIL_VERIFICATION_SENT_ONCE_KEY = 'email_verification_sent_once';

const hasSentEmailVerificationOnce = (): boolean => {
  try {
    return (
      window.sessionStorage.getItem(EMAIL_VERIFICATION_SENT_ONCE_KEY) === 'true'
    );
  } catch {
    return false;
  }
};

const markSentEmailVerificationOnce = (): void => {
  try {
    window.sessionStorage.setItem(EMAIL_VERIFICATION_SENT_ONCE_KEY, 'true');
  } catch {
    // ignore
  }
};

const formatGraceDeadline = (iso?: string | null): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
};

const formatSendEmailError = (
  e: unknown,
  { auto }: { auto: boolean },
): string => {
  const err = e as { status?: number; data?: unknown } | undefined;
  const status = err?.status;
  const data = err?.data as { error?: string } | undefined;
  const isRateLimited = status === 429 || data?.error === 'rate_limited';
  if (isRateLimited) {
    return 'Too many attempts. Please wait a few minutes and try again.';
  }
  return auto
    ? 'Failed to send verification email. You can try again below.'
    : 'Failed to send verification email. Please try again.';
};

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const redirect = useMemo(
    () =>
      normalizePostAuthRedirect(searchParams.get('redirect'), {
        extraBlockedPrefixes: ['/mfa', '/verify-email'],
      }),
    [searchParams],
  );

  const { data: authStatus, error } = useGetAuthStatusQuery(undefined, {
    pollingInterval: 3_000,
    refetchOnMountOrArgChange: true,
  });

  const [sendEmailVerification, sendResult] =
    useSendEmailVerificationMutation();
  const [banner, setBanner] = useState<{
    severity: 'success' | 'error';
    text: string;
  } | null>(null);
  const [autoSendAttempted, setAutoSendAttempted] = useState(false);
  const [sentOnce, setSentOnce] = useState(hasSentEmailVerificationOnce());
  const isMfaPrompt = authStatus?.mfaPrompt === true;
  const isMfaRequired = authStatus?.mfaRequired === true;
  const canSkip = authStatus?.mfaCanSkip === true && isMfaPrompt;

  useEffect(() => {
    track('auth_verify_email_viewed', {
      redirect,
    });
  }, [redirect]);

  useEffect(() => {
    const err = error as { status?: number } | undefined;
    if (err?.status === 401) {
      const currentPath = window.location.pathname + window.location.search;
      redirectToSignin(currentPath);
    }
  }, [error]);

  useEffect(() => {
    if (!authStatus?.emailVerified) return;

    const needsMfa = !!authStatus.mfaRequired || !!authStatus.mfaPrompt;
    if (needsMfa) {
      navigate(
        `/mfa${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ''}`,
        {
          replace: true,
        },
      );
      return;
    }

    navigate(redirect || '/', { replace: true });
  }, [
    authStatus?.emailVerified,
    authStatus?.mfaPrompt,
    authStatus?.mfaRequired,
    navigate,
    redirect,
  ]);

  useEffect(() => {
    if (autoSendAttempted) return;
    if (sentOnce) return;
    if (!authStatus) return;
    if (authStatus.emailVerified) return;
    if (!isMfaRequired) return;

    setAutoSendAttempted(true);
    // Fire-and-forget: show a banner if it succeeds or fails.
    // If the user is rate limited, they can retry later via the button.
    sendEmailVerification()
      .unwrap()
      .then(() => {
        markSentEmailVerificationOnce();
        setSentOnce(true);
        track('auth_verify_email_send_success', { auto: true, redirect });
        setBanner({
          severity: 'success',
          text: 'Verification email sent. Check your inbox (and spam folder).',
        });
      })
      .catch((e) => {
        const err = e as { status?: number } | undefined;
        if (err?.status === 401) {
          const currentPath = window.location.pathname + window.location.search;
          redirectToSignin(currentPath);
          return;
        }
        track('auth_verify_email_send_failed', {
          auto: true,
          http_status: err?.status,
          redirect,
        });
        setBanner({
          severity: 'error',
          text: formatSendEmailError(e, { auto: true }),
        });
      });
  }, [
    authStatus,
    autoSendAttempted,
    isMfaRequired,
    sendEmailVerification,
    sentOnce,
  ]);

  const handleResend = async () => {
    setBanner(null);
    try {
      await sendEmailVerification().unwrap();
      markSentEmailVerificationOnce();
      setSentOnce(true);
      track('auth_verify_email_send_success', { auto: false, redirect });
      setBanner({
        severity: 'success',
        text: 'Verification email sent. Check your inbox (and spam folder).',
      });
    } catch (e) {
      const err = e as { status?: number } | undefined;
      if (err?.status === 401) {
        const currentPath = window.location.pathname + window.location.search;
        redirectToSignin(currentPath);
        return;
      }
      track('auth_verify_email_send_failed', {
        auto: false,
        http_status: err?.status,
        redirect,
      });
      setBanner({
        severity: 'error',
        text: formatSendEmailError(e, { auto: false }),
      });
    }
  };

  const handleSkip = () => {
    try {
      const until = authStatus?.graceExpiresAt
        ? Date.parse(authStatus.graceExpiresAt)
        : Date.now() + 24 * 60 * 60 * 1000;
      window.localStorage.setItem(MFA_PROMPT_SKIPPED_UNTIL_KEY, String(until));
    } catch {
      // ignore
    }
    track('auth_verify_email_skipped', { redirect });
    navigate(redirect || '/', { replace: true });
  };

  const showGraceWarning = canSkip && authStatus.emailVerified === false;
  const graceDeadline = formatGraceDeadline(authStatus?.graceExpiresAt);
  let statusAlert: ReactNode = null;
  if (authStatus?.emailVerified === false) {
    if (showGraceWarning) {
      const deadlineText = graceDeadline
        ? `Your grace period ends on ${graceDeadline}.`
        : 'Your grace period will end soon.';
      statusAlert = (
        <Alert className="mb-4" severity="warning">
          We’re rolling out multi-factor authentication (MFA) for all accounts.
          You can skip MFA for now, but you’ll need to confirm your email and
          set up MFA before it becomes required. {deadlineText} After that, MFA
          will be required to sign in.
        </Alert>
      );
    } else {
      statusAlert = (
        <Alert className="mb-4" severity="info">
          We’ll automatically continue once your email is verified.
        </Alert>
      );
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-sage-100 px-6 py-10">
      <Card className="w-full max-w-xl">
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Verify your email
          </Typography>
          <Typography className="mb-4" color="text.secondary">
            To continue setting up multi-factor authentication, verify your
            email by clicking the link we send you.
          </Typography>

          {banner ? (
            <Alert className="mb-4" severity={banner.severity}>
              {banner.text}
            </Alert>
          ) : null}

          {statusAlert}

          <Button
            disabled={sendResult.isLoading}
            variant="contained"
            fullWidth
            onClick={handleResend}
          >
            {showGraceWarning
              ? 'Send verification email'
              : 'Resend verification email'}
          </Button>

          {canSkip ? (
            <Button
              className="mt-2"
              disabled={sendResult.isLoading}
              variant="outlined"
              fullWidth
              onClick={handleSkip}
            >
              Skip for now
            </Button>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
